/**
 * Automatically adds/removes the free gift (Vela Sorpresa) line item when
 * the cart subtotal (excluding the auto-gift itself) crosses 80,00€. The
 * actual 100%-off discount is configured in Shopify Admin (Discounts) and is
 * not touched here — this script only manages whether the line item exists.
 *
 * The auto-added line is tagged with the _regalo_automatico line item
 * property (leading underscore keeps it hidden from the customer at
 * checkout). That's what tells it apart from a Vela Sorpresa the customer
 * adds manually at full price — same variant, so without a distinguishing
 * property Shopify would merge the two into a single cart line. This script
 * only ever adds/removes the tagged line; a manual line is left alone.
 *
 * It never adds a new (tagged) line if a Vela Sorpresa of any kind is
 * already in the cart. Shopify's native "Buy X Get Y" discount can't be
 * conditioned on line item properties, so it discounts whichever eligible
 * Vela Sorpresa line already exists once the threshold is crossed — if
 * that's a manual line, adding a second tagged one would just create an
 * undiscounted duplicate the customer gets charged for.
 */
(function () {
  var GIFT_VARIANT_ID = 62608056746371;
  var GIFT_THRESHOLD_CENTS = 8000;
  var GIFT_PROPERTY = '_regalo_automatico';

  // Locked for the entire read-decide-act cycle (not just the add/remove
  // request itself) so two near-simultaneous triggers — e.g. a page-load
  // check racing a cart:update event fired a moment later — can't both
  // read "gift missing" before either has written it, and double-add.
  var isBusy = false;

  function isAutoGiftLine(item) {
    return item.variant_id === GIFT_VARIANT_ID && item.properties && item.properties[GIFT_PROPERTY] === 'true';
  }

  function getGiftLine(cart) {
    for (var i = 0; i < cart.items.length; i++) {
      if (isAutoGiftLine(cart.items[i])) {
        return cart.items[i];
      }
    }
    return null;
  }

  // Any Vela Sorpresa line at all, tagged or not. Shopify's native "Buy X
  // Get Y" discount can't be conditioned on line item properties: it
  // allocates its 100%-off benefit to whichever eligible Vela Sorpresa line
  // already exists in the cart the moment the threshold is crossed, not
  // necessarily the tagged one. If a manual line is already present when
  // the threshold is met, Shopify discounts THAT line itself. Adding a
  // second (tagged) line on top would only create a duplicate that Shopify
  // does not also discount (its per-order limit is already spent),
  // leaving the customer charged full price for the "gift". So the add
  // decision below checks for any Vela Sorpresa line, not just a tagged
  // one, to avoid ever creating that duplicate.
  function getAnyGiftLine(cart) {
    for (var i = 0; i < cart.items.length; i++) {
      if (cart.items[i].variant_id === GIFT_VARIANT_ID) {
        return cart.items[i];
      }
    }
    return null;
  }

  function getEligibleSubtotal(cart) {
    var subtotal = 0;
    for (var i = 0; i < cart.items.length; i++) {
      var item = cart.items[i];
      // Only the auto-added gift line is excluded — a manually-added Vela
      // Sorpresa (no property, full price) counts toward the threshold like
      // any other product.
      if (isAutoGiftLine(item)) continue;
      subtotal += item.line_price;
    }
    return subtotal;
  }

  function refreshCartUI(cart) {
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-gift-threshold', cartData: cart });
    }
  }

  function addGift() {
    var properties = {};
    properties[GIFT_PROPERTY] = 'true';

    return fetch(window.routes.cart_add_url + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: GIFT_VARIANT_ID, quantity: 1, properties: properties }] }),
    })
      .then(function () {
        return fetch(window.routes.cart_url + '.js');
      })
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        refreshCartUI(cart);
      })
      .catch(function (error) {
        console.error('cart-gift-threshold: failed to add gift', error);
      });
  }

  function removeGift(giftLine) {
    return fetch(window.routes.cart_change_url + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: giftLine.key, quantity: 0 }),
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        refreshCartUI(cart);
      })
      .catch(function (error) {
        console.error('cart-gift-threshold: failed to remove gift', error);
      });
  }

  function evaluate() {
    if (isBusy) return;
    isBusy = true;

    fetch(window.routes.cart_url + '.js')
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        var eligibleSubtotal = getEligibleSubtotal(cart);
        var taggedGiftLine = getGiftLine(cart);
        var anyGiftLine = getAnyGiftLine(cart);
        var meetsThreshold = eligibleSubtotal >= GIFT_THRESHOLD_CENTS;

        if (meetsThreshold && !anyGiftLine) {
          return addGift();
        } else if (!meetsThreshold && taggedGiftLine) {
          // Only ever remove the line this script added itself — a manual
          // line is the customer's own purchase and is never touched here.
          return removeGift(taggedGiftLine);
        }
      })
      .catch(function (error) {
        console.error('cart-gift-threshold: failed to evaluate cart', error);
      })
      .finally(function () {
        isBusy = false;
      });
  }

  document.addEventListener('DOMContentLoaded', function () {
    try {
      evaluate();
    } catch (error) {
      console.error('cart-gift-threshold: unexpected error', error);
    }
  });

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    // Belt-and-braces: pubsub.js already isolates subscriber failures on
    // its own, but this feature specifically must never be able to affect
    // the rest of the cart, so it also guards itself here.
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      try {
        if (event && event.source === 'cart-gift-threshold') return;
        evaluate();
      } catch (error) {
        console.error('cart-gift-threshold: unexpected error', error);
      }
    });
  }
})();
